import { useEffect, useMemo, useState } from 'react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { RebateAgreementListItem } from 'src/api/rebates/types';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';

interface BulkDuplicateConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (year: number, agreements: RebateAgreementListItem[]) => void;
  agreements: RebateAgreementListItem[];
  loading: boolean;
}

const getUtcYear = (dateValue: string) => {
  const parsedDate = new Date(`${dateValue}T00:00:00Z`);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate.getUTCFullYear();
};

const isMultiYearAgreement = (agreement: RebateAgreementListItem) => {
  const startYear = getUtcYear(agreement.start_date);
  const endYear = getUtcYear(agreement.end_date);
  if (startYear === null || endYear === null) {
    return false;
  }
  return startYear !== endYear;
};

const doesAgreementOverlapYear = (
  agreement: RebateAgreementListItem,
  year: number,
) => {
  const startDate = new Date(`${agreement.start_date}T00:00:00Z`);
  const endDate = new Date(`${agreement.end_date}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearEnd = new Date(Date.UTC(year, 11, 31));
  return startDate <= yearEnd && endDate >= yearStart;
};

const formatAgreementLabel = (agreement: RebateAgreementListItem) =>
  `${agreement.name} (${agreement.supplier.name})`;

export function BulkDuplicateConfirmDialog({
  open,
  onClose,
  onConfirm,
  agreements,
  loading,
}: BulkDuplicateConfirmDialogProps) {
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState<string>(currentYear.toString());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset year and selections when dialog opens
  useEffect(() => {
    if (open) {
      setYear(currentYear.toString());
      setSelectedIds(new Set(agreements.map((agreement) => agreement.id)));
    }
  }, [open, currentYear, agreements]);

  const yearNumber = parseInt(year, 10);
  const isValidYear =
    !isNaN(yearNumber) && yearNumber >= 1900 && yearNumber <= 2100;
  const count = agreements.length;

  const { data: targetYearAgreements } = useRebateAgreements(
    isValidYear ? { years: [yearNumber] } : undefined,
    isValidYear,
  );

  const multiYearAgreements = useMemo(
    () => agreements.filter(isMultiYearAgreement),
    [agreements],
  );

  const agreementsWithExistingInTargetYear = useMemo(() => {
    if (!isValidYear || !targetYearAgreements) {
      return [];
    }

    return agreements.filter((agreement) =>
      targetYearAgreements.some(
        (existing) =>
          existing.supplier_id === agreement.supplier_id &&
          doesAgreementOverlapYear(existing, yearNumber),
      ),
    );
  }, [agreements, isValidYear, targetYearAgreements, yearNumber]);

  const warningMessages = useMemo(() => {
    const warnings: string[] = [];

    if (multiYearAgreements.length > 0) {
      warnings.push(
        `Multi-year agreements selected. Review date ranges: ${multiYearAgreements
          .map(formatAgreementLabel)
          .join(', ')}.`,
      );
    }

    if (agreementsWithExistingInTargetYear.length > 0 && isValidYear) {
      warnings.push(
        `Suppliers already have agreements in ${yearNumber}: ${agreementsWithExistingInTargetYear
          .map(formatAgreementLabel)
          .join(', ')}.`,
      );
    }

    return warnings;
  }, [
    agreementsWithExistingInTargetYear,
    isValidYear,
    multiYearAgreements,
    yearNumber,
  ]);

  const warnedAgreements = useMemo(() => {
    const warnedIds = new Set(
      [...multiYearAgreements, ...agreementsWithExistingInTargetYear].map(
        (agreement) => agreement.id,
      ),
    );
    return agreements.filter((agreement) => warnedIds.has(agreement.id));
  }, [agreements, agreementsWithExistingInTargetYear, multiYearAgreements]);

  const selectedAgreements = useMemo(
    () => agreements.filter((agreement) => selectedIds.has(agreement.id)),
    [agreements, selectedIds],
  );

  const toggleAgreement = (agreementId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(agreementId)) {
        next.delete(agreementId);
      } else {
        next.add(agreementId);
      }
      return next;
    });
  };

  const excludeWarnedAgreements = () => {
    if (warnedAgreements.length === 0) {
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      warnedAgreements.forEach((agreement) => next.delete(agreement.id));
      return next;
    });
  };

  const handleConfirm = () => {
    if (isValidYear) {
      onConfirm(yearNumber, selectedAgreements);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={`Duplicate ${count} Rebate Agreement${count !== 1 ? 's' : ''}`}
      confirmText={`Duplicate ${selectedAgreements.length} Agreement${selectedAgreements.length !== 1 ? 's' : ''}`}
      loading={loading}
      disabled={!isValidYear || selectedAgreements.length === 0}
      wide
    >
      <ScrollArea className="max-h-[60vh] pr-3">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The following {count} agreement{count !== 1 ? 's' : ''} will be
            duplicated with dates set to January 1 - December 31 of the selected
            year and set to draft mode. {selectedAgreements.length} selected.
          </p>

          {warningMessages.length > 0 && (
            <Accordion type="single" collapsible>
              <AccordionItem
                value="warnings"
                className="border border-amber-200 rounded-md bg-amber-50 px-3"
              >
                <AccordionTrigger className="py-2 text-amber-900 hover:no-underline">
                  Warnings ({warningMessages.length})
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <ScrollArea className="h-64 pr-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium text-amber-900">
                        Review before duplicating
                      </p>
                      {warnedAgreements.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={excludeWarnedAgreements}
                          className="border-amber-300 text-amber-900"
                        >
                          Exclude warned agreements
                        </Button>
                      )}
                    </div>
                    <ul className="mt-2 list-disc list-inside text-sm text-amber-800">
                      {warningMessages.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* List of agreements to be duplicated */}
          <ScrollArea className="h-40 rounded-md border p-3">
            <ul className="space-y-2">
              {agreements.map((agreement) => {
                const isSelected = selectedIds.has(agreement.id);
                return (
                  <li key={agreement.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleAgreement(agreement.id)}
                      aria-label={`Select ${agreement.name}`}
                    />
                    <span
                      className={`text-sm ${
                        isSelected
                          ? 'text-foreground'
                          : 'text-muted-foreground line-through'
                      }`}
                    >
                      <span className="font-medium">{agreement.name}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        ({agreement.supplier.name})
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>

          <div className="space-y-2">
            <Label htmlFor="duplicate-year" className="text-sm font-normal">
              Year to duplicate to:
            </Label>
            <Input
              id="duplicate-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="Enter year"
              className="w-full"
              min={1900}
              max={2100}
              autoFocus
            />
            {!isValidYear && year !== '' && (
              <p className="text-sm text-destructive">
                Please enter a valid year between 1900 and 2100
              </p>
            )}
          </div>
        </div>
      </ScrollArea>
    </ConfirmDialog>
  );
}
